import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import salesOrdersService from "../../../services/legacy/salesOrdersService";
import salesOrderLinesService from "../../../services/legacy/salesOrderLinesService";
import customersService from "../../../services/legacy/customersService";
import { getFieldLoose, estadoPvBadgeStyle, formatDateOnly, toNumberOrNull, NOT_SYNCED_BADGE_STYLE, NOT_SYNCED_LABEL } from "./seleccionBobinasUtils";

const FILTER_OPTIONS = [
  { value: "all", labelKey: "seleccionBobinas.tray.filterAll" },
  { value: "notReviewed", labelKey: "seleccionBobinas.tray.filterNotReviewed" },
  { value: "reviewed", labelKey: "seleccionBobinas.tray.filterReviewed" },
  { value: "notReviewedNoOm", labelKey: "seleccionBobinas.tray.filterNotReviewedNoOm" },
  { value: "reviewedNoOm", labelKey: "seleccionBobinas.tray.filterReviewedNoOm" },
];

const ESTADO_LABEL_KEYS = {
  0: "axEnums.CMTSeleccionBobinasEstadoPV.0",
  1: "axEnums.CMTSeleccionBobinasEstadoPV.1",
  2: "axEnums.CMTSeleccionBobinasEstadoPV.2",
  3: "axEnums.CMTSeleccionBobinasEstadoPV.3",
};

const PAGE_SIZE = 300;

export default function SeleccionBobinasPvsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerNames, setCustomerNames] = useState({});
  const [noOmByOrder, setNoOmByOrder] = useState({});
  const [checkingNoOm, setCheckingNoOm] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await salesOrdersService.getAll(0, PAGE_SIZE, [], {});
      const content = Array.isArray(result?.content) ? result.content : [];
      setRows(content);
    } catch (err) {
      console.error("Error loading sales orders for seleccion bobinas tray:", err);
      setError(t("seleccionBobinas.tray.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Resolve customer display names once per unique custaccount so the tray
  // shows a human-readable customer instead of just the account number.
  useEffect(() => {
    const accounts = [...new Set(rows.map((row) => String(getFieldLoose(row, "custaccount") || "").trim()).filter(Boolean))];
    const missing = accounts.filter((acc) => !(acc in customerNames));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(missing.map(async (acc) => {
        try {
          const page = await customersService.getAll(0, 1, [], { accountNum: acc });
          const customer = Array.isArray(page?.content) ? page.content[0] : null;
          return [acc, customer ? String(getFieldLoose(customer, "name") || acc) : acc];
        } catch {
          return [acc, acc];
        }
      }));
      if (!cancelled) {
        setCustomerNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    })();
    return () => { cancelled = true; };
  }, [rows, customerNames]);

  // Returns null (not 0) when the field has not been synced yet from AX4, so
  // callers can distinguish "genuinely No enviado" from "unknown/not synced".
  const estadoOf = useCallback((row) => {
    return toNumberOrNull(getFieldLoose(row, "cmtseleccionbobinasestadopv"));
  }, []);

  const identityOf = useCallback((row) => ({
    iSalesId: String(getFieldLoose(row, "iSalesId") || ""),
    dataAreaId: String(getFieldLoose(row, "dataAreaId") || ""),
  }), []);

  // "Sin OM" (no manufacturing order) is approximated client-side: a sales
  // order is considered "sin OM" when none of its lines have an mfoid
  // assigned yet. There is no dedicated backend aggregate for this, so it is
  // only computed on demand (when one of the *NoOm filters is selected) and
  // only for the rows currently matching the base status filter, to keep the
  // number of extra requests bounded.
  const loadNoOmFlags = useCallback(async (candidateRows) => {
    setCheckingNoOm(true);
    try {
      const entries = await Promise.all(candidateRows.map(async (row) => {
        const { iSalesId, dataAreaId } = identityOf(row);
        if (!iSalesId) return [iSalesId, true];
        try {
          const linesResult = await salesOrderLinesService.getAll(0, 200, [], { dataAreaId, iSalesId });
          const lines = Array.isArray(linesResult?.content) ? linesResult.content : [];
          const hasOm = lines.some((line) => String(getFieldLoose(line, "mfoid") || "").trim() !== "");
          return [iSalesId, !hasOm];
        } catch {
          return [iSalesId, true];
        }
      }));
      setNoOmByOrder((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    } finally {
      setCheckingNoOm(false);
    }
  }, [identityOf]);

  useEffect(() => {
    if (statusFilter !== "notReviewedNoOm" && statusFilter !== "reviewedNoOm") return;
    const wantsReviewed = statusFilter === "reviewedNoOm";
    const candidates = rows.filter((row) => {
      const estado = estadoOf(row);
      return wantsReviewed ? estado === 2 : estado !== 2;
    });
    const pending = candidates.filter((row) => {
      const { iSalesId } = identityOf(row);
      return !(iSalesId in noOmByOrder);
    });
    if (pending.length > 0) {
      loadNoOmFlags(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (statusFilter === "notReviewed") {
      list = list.filter((row) => estadoOf(row) !== 2);
    } else if (statusFilter === "reviewed") {
      list = list.filter((row) => estadoOf(row) === 2);
    } else if (statusFilter === "notReviewedNoOm") {
      list = list.filter((row) => estadoOf(row) !== 2 && noOmByOrder[identityOf(row).iSalesId] === true);
    } else if (statusFilter === "reviewedNoOm") {
      list = list.filter((row) => estadoOf(row) === 2 && noOmByOrder[identityOf(row).iSalesId] === true);
    }

    const needle = customerFilter.trim().toLowerCase();
    if (needle) {
      list = list.filter((row) => {
        const account = String(getFieldLoose(row, "custaccount") || "").toLowerCase();
        const name = String(customerNames[getFieldLoose(row, "custaccount")] || "").toLowerCase();
        return account.includes(needle) || name.includes(needle);
      });
    }

    return [...list].sort((a, b) => {
      const da = getFieldLoose(a, "requesteddelivdate") || "";
      const db = getFieldLoose(b, "requesteddelivdate") || "";
      return String(db).localeCompare(String(da));
    });
  }, [rows, statusFilter, customerFilter, customerNames, noOmByOrder, estadoOf, identityOf]);

  const openOrder = useCallback((row) => {
    const { iSalesId, dataAreaId } = identityOf(row);
    if (!iSalesId) return;
    const encodedId = encodeURIComponent(iSalesId);
    const path = dataAreaId
      ? `/legacy/selector-bobinas/${encodeURIComponent(dataAreaId)}/${encodedId}`
      : `/legacy/selector-bobinas/document/${encodedId}`;
    navigate(path);
  }, [identityOf, navigate]);

  return (
    <div className="page-container">
      <h2>{t("seleccionBobinas.tray.title")}</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, fontWeight: 600 }}>
          {t("seleccionBobinas.tray.filterLabel")}
          <select
            className="form-control"
            style={{ minWidth: 260 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: 12, fontWeight: 600 }}>
          {t("seleccionBobinas.tray.customerFilterLabel")}
          <input
            type="text"
            className="form-control"
            style={{ minWidth: 220 }}
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            placeholder={t("seleccionBobinas.tray.customerFilterPlaceholder")}
          />
        </label>
        <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: "flex-end" }} onClick={loadOrders}>
          {t("seleccionBobinas.tray.refresh")}
        </button>
        {checkingNoOm && <span style={{ alignSelf: "flex-end", fontSize: 12, color: "#6b7280" }}>{t("seleccionBobinas.tray.checkingNoOm")}</span>}
      </div>

      {error && <div className="modern-alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ overflowX: "auto" }}>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>{t("seleccionBobinas.tray.columnStatus")}</th>
              <th>{t("seleccionBobinas.tray.columnSentBy")}</th>
              <th>{t("seleccionBobinas.tray.columnDeliveryDate")}</th>
              <th>{t("seleccionBobinas.tray.columnOrderNum")}</th>
              <th>{t("seleccionBobinas.tray.columnCustomer")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5}>{t("seleccionBobinas.tray.loading")}</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={5}>{t("seleccionBobinas.tray.noResults")}</td></tr>
            )}
            {!loading && filteredRows.map((row) => {
              const estado = estadoOf(row);
              const account = getFieldLoose(row, "custaccount");
              const customerLabel = customerNames[account] || account || "";
              const salesId = getFieldLoose(row, "salesId") || getFieldLoose(row, "iSalesId");
              return (
                <tr
                  key={`${getFieldLoose(row, "dataAreaId") || ""}:${getFieldLoose(row, "iSalesId") || ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => openOrder(row)}
                >
                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 10px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        ...(estado === null ? NOT_SYNCED_BADGE_STYLE : estadoPvBadgeStyle(estado)),
                      }}
                      title={estado === null ? t("seleccionBobinas.notSyncedHint") : undefined}
                    >
                      {estado === null ? NOT_SYNCED_LABEL : t(ESTADO_LABEL_KEYS[estado] || ESTADO_LABEL_KEYS[0])}
                    </span>
                  </td>
                  <td>{getFieldLoose(row, "cmtseleccionbobinasenviadopor") || ""}</td>
                  <td>{formatDateOnly(getFieldLoose(row, "requesteddelivdate"))}</td>
                  <td>{salesId}</td>
                  <td>{customerLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
